import { type CSSProperties, type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Search,
  Truck,
  Boxes,
  FileText,
  Wrench,
  UserPlus,
  CalendarDays,
  ChevronRight,
  CheckCircle2,
  Pencil,
  GripVertical,
  Check,
  RotateCcw,
  Eye,
  EyeOff,
  Plus,
  Ban,
  Trash2,
  Link as LinkIcon,
  Star,
  Zap,
  Package,
  Phone,
  Mail,
  Euro,
  Bell,
  Building2,
  Users,
  ClipboardList,
} from 'lucide-react';
import { applyLayoutOrder, emptyUiLayout, textOverride, type CustomTile, type TileSize, type UiLayout, type UiLayoutGateway } from '@forq/platform-data';
import { usePlatform } from '@forq/platform';
import { availableProfiles, defaultProfile, profileByKey, type DashboardMetrics, type DashboardProfileKey } from './dashboardProfiles';
import { DEFAULT_TILE_COLOR, tileColorStyle } from '@forq/ui';
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy, sortableKeyboardCoordinates, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import './start.css';

/** Was das Startbild braucht -- alles kommt aus der App-Komposition (Signal, Command, Platform). */
export interface StartTask {
  readonly id: string;
  readonly title: string;
  readonly meta: string;
  readonly tone: 'normal' | 'warn' | 'danger' | string;
  readonly route: string;
}

interface StartPageProps {
  readonly userName: string;
  readonly permissions: { has(permission: string): boolean };
  readonly tasks: readonly StartTask[];
  readonly onOpenCommand: () => void;
  readonly onNavigate: (route: string) => void;
  readonly tenantId: string;
  /** Nur Admins dürfen Reihenfolge, Texte, Sichtbarkeit + eigene Kacheln für den Mandanten ändern. */
  readonly canCustomize: boolean;
  readonly layoutGateway: UiLayoutGateway;
  /** Live-Kennzahlen fürs Dashboard (aus den Domänen-Gateways, je nach Rechten befüllt). */
  readonly metrics: DashboardMetrics;
}

interface StartAction {
  readonly key: string;
  readonly label: string;
  readonly hint: string;
  readonly icon: ReactNode;
  readonly route: string;
  /** Nötige Sicht-Berechtigung -- die Kachel erscheint nur, wenn man den Bereich sehen darf. */
  readonly permission: string;
  readonly primary?: boolean;
}

// Die häufigsten Startpunkte. Reihenfolge + Texte hier sind nur der Standard; der Admin überschreibt
// sie je Mandant im „Anpassen"-Modus und kann eigene Kacheln ergänzen.
const ACTIONS: readonly StartAction[] = [
  { key: 'rental', label: 'Neue Miete', hint: 'Gerät vermieten', icon: <Truck size={22} />, route: '/mietpark?neu', permission: 'rental.view', primary: true },
  { key: 'machine', label: 'Maschine aufnehmen', hint: 'In den Stamm', icon: <Boxes size={22} />, route: '/maschinen?neu', permission: 'machines.view' },
  { key: 'invoice', label: 'Rechnung schreiben', hint: 'Aus einem Vorgang', icon: <FileText size={22} />, route: '/finance/abrechnung', permission: 'finance.view' },
  { key: 'service', label: 'Service annehmen', hint: 'Reparatur / Wartung', icon: <Wrench size={22} />, route: '/vorgaenge?neu', permission: 'orders.view' },
  { key: 'customer', label: 'Kunde anlegen', hint: 'Neuer Kontakt', icon: <UserPlus size={22} />, route: '/kunden?neu', permission: 'customers.view' },
  { key: 'calendar', label: 'Mietkalender', hint: 'Wer hat was, wann', icon: <CalendarDays size={22} />, route: '/mietpark', permission: 'rental.view' },
];

// Symbol-Auswahl für eigene Kacheln — Name wird gespeichert, hier zur Anzeige aufgelöst.
const ICON_CHOICES: readonly { readonly name: string; readonly label: string; readonly node: ReactNode }[] = [
  { name: 'Link', label: 'Link', node: <LinkIcon size={22} /> },
  { name: 'Star', label: 'Stern', node: <Star size={22} /> },
  { name: 'Zap', label: 'Blitz', node: <Zap size={22} /> },
  { name: 'Package', label: 'Paket', node: <Package size={22} /> },
  { name: 'Truck', label: 'LKW', node: <Truck size={22} /> },
  { name: 'Boxes', label: 'Kisten', node: <Boxes size={22} /> },
  { name: 'Wrench', label: 'Werkzeug', node: <Wrench size={22} /> },
  { name: 'FileText', label: 'Dokument', node: <FileText size={22} /> },
  { name: 'ClipboardList', label: 'Liste', node: <ClipboardList size={22} /> },
  { name: 'CalendarDays', label: 'Kalender', node: <CalendarDays size={22} /> },
  { name: 'Phone', label: 'Telefon', node: <Phone size={22} /> },
  { name: 'Mail', label: 'E-Mail', node: <Mail size={22} /> },
  { name: 'Euro', label: 'Euro', node: <Euro size={22} /> },
  { name: 'Bell', label: 'Glocke', node: <Bell size={22} /> },
  { name: 'Building2', label: 'Gebäude', node: <Building2 size={22} /> },
  { name: 'Users', label: 'Personen', node: <Users size={22} /> },
];
const ICON_MAP: Readonly<Record<string, ReactNode>> = Object.fromEntries(ICON_CHOICES.map((choice) => [choice.name, choice.node]));
const iconFor = (name: string): ReactNode => ICON_MAP[name] ?? ICON_MAP.Link;

const HEADING_ACTIONS = 'heading:actions';
const HEADING_TODAY = 'heading:today';
const DEFAULT_HEADING_ACTIONS = 'Was möchtest du tun?';
const DEFAULT_HEADING_TODAY = 'Das steht heute an';
const labelKey = (key: string) => `tile:${key}:label`;
const hintKey = (key: string) => `tile:${key}:hint`;
const SIZE_LABELS: Readonly<Record<TileSize, string>> = { compact: 'Kompakt', normal: 'Normal', large: 'Groß' };
const SIZE_ORDER: readonly TileSize[] = ['compact', 'normal', 'large'];

/** Anzeige-Kachel (eingebaut oder eigen) — vereinheitlicht fürs Rendern. */
interface DisplayTile {
  readonly key: string;
  readonly label: string;
  readonly hint: string;
  readonly icon: ReactNode;
  readonly target: string;
  readonly primary: boolean;
  readonly isCustom: boolean;
}

function greeting(hour: number): string {
  if (hour < 11) return 'Guten Morgen';
  if (hour < 18) return 'Guten Tag';
  return 'Guten Abend';
}

function firstName(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return '';
  return trimmed.includes(' ') ? trimmed.split(' ')[0] : trimmed;
}

interface StartEditTileProps {
  readonly id: string;
  readonly action?: StartAction;
  readonly custom?: CustomTile;
  readonly hidden: boolean;
  readonly color?: string;
  readonly labelValue: string;
  readonly hintValue: string;
  readonly onToggleHidden: (key: string) => void;
  readonly onSetColor: (key: string, color: string | null) => void;
  readonly onSetLabel: (key: string, value: string) => void;
  readonly onSetHint: (key: string, value: string) => void;
  readonly onPatchCustom: (key: string, patch: Partial<CustomTile>) => void;
  readonly onRemoveCustom: (key: string) => void;
}

/** Eine sortierbare Start-Kachel im Bearbeiten-Modus (dnd-kit): Griff = Ziehpunkt, alles andere bleibt bedienbar. */
function StartEditTile({ id, action, custom, hidden, color, labelValue, hintValue, onToggleHidden, onSetColor, onSetLabel, onSetHint, onPatchCustom, onRemoveCustom }: StartEditTileProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style: CSSProperties = { ...(color ? tileColorStyle(color) : {}), transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 3 : undefined };
  const cls = `forq-start__tile${action?.primary ? ' is-primary' : ''}${isDragging ? ' is-dragging' : ''}${hidden ? ' is-hidden' : ''}${custom ? ' is-custom' : ''}`;
  return (
    <div ref={setNodeRef} className={cls} style={style}>
      <span className="forq-start__tile-grip" {...attributes} {...listeners} title="Ziehen zum Verschieben"><GripVertical size={16} /></span>
      <button type="button" className="forq-start__tile-hide" onClick={() => onToggleHidden(id)} title={hidden ? 'Wieder einblenden' : 'Ausblenden'}>
        {hidden ? <EyeOff size={15} /> : <Eye size={15} />}
      </button>
      <label className="forq-start__tile-colorpick" title="Kachelfarbe">
        <input type="color" value={color ?? DEFAULT_TILE_COLOR} onChange={(event) => onSetColor(id, event.target.value)} />
      </label>
      {color ? (
        <button type="button" className="forq-start__tile-color-clear" onClick={() => onSetColor(id, null)} title="Farbe entfernen"><Ban size={14} /></button>
      ) : null}
      <span className="forq-start__tile-icon">{custom ? iconFor(custom.icon) : action?.icon}</span>
      {custom ? (
        <span className="forq-start__tile-text">
          <input className="forq-start__tile-input is-label" value={custom.label} placeholder="Beschriftung" onChange={(event) => onPatchCustom(id, { label: event.target.value })} aria-label="Beschriftung" />
          <input className="forq-start__tile-input is-hint" value={custom.hint} placeholder="Unter-Text (optional)" onChange={(event) => onPatchCustom(id, { hint: event.target.value })} aria-label="Unter-Text" />
          <div className="forq-start__tile-custom">
            <select className="forq-start__tile-input" value={custom.icon} onChange={(event) => onPatchCustom(id, { icon: event.target.value })} aria-label="Symbol">
              {ICON_CHOICES.map((choice) => <option key={choice.name} value={choice.name}>{choice.label}</option>)}
            </select>
            <input className="forq-start__tile-input" value={custom.target} placeholder="/seite oder https://…" onChange={(event) => onPatchCustom(id, { target: event.target.value })} aria-label="Ziel" />
            <button type="button" className="forq-start__tile-delete" onClick={() => onRemoveCustom(id)} title="Kachel löschen" aria-label="Kachel löschen"><Trash2 size={15} /></button>
          </div>
        </span>
      ) : (
        <span className="forq-start__tile-text">
          <input className="forq-start__tile-input is-label" value={labelValue} onChange={(event) => onSetLabel(id, event.target.value)} aria-label="Beschriftung" />
          <input className="forq-start__tile-input is-hint" value={hintValue} onChange={(event) => onSetHint(id, event.target.value)} aria-label="Unter-Text" />
        </span>
      )}
    </div>
  );
}

export function StartPage({ userName, permissions, tasks, onOpenCommand, onNavigate, tenantId, canCustomize, layoutGateway, metrics }: StartPageProps) {
  const now = useMemo(() => new Date(), []);
  const hello = `${greeting(now.getHours())}${firstName(userName) ? `, ${firstName(userName)}` : ''}`;
  const dateLabel = now.toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' });
  const today = tasks.slice(0, 5);

  // Rollen-Profil aus den Rechten ableiten, mit Umschalter. Auswahl je Mandant gemerkt.
  const profiles = useMemo(() => availableProfiles(permissions), [permissions]);
  const storageKey = `forq.dashboard.profile.${tenantId}`;
  const [profile, setProfile] = useState<DashboardProfileKey>(() => {
    try {
      const stored = window.localStorage.getItem(storageKey);
      if (stored && profiles.some((entry) => entry.key === stored)) return stored as DashboardProfileKey;
    } catch { /* localStorage evtl. gesperrt */ }
    return defaultProfile(permissions);
  });
  useEffect(() => {
    // Bei Mandanten-/Rechtewechsel ein ungültig gewordenes Profil auf den Standard zurückziehen.
    setProfile((current) => (profiles.some((entry) => entry.key === current) ? current : defaultProfile(permissions)));
  }, [profiles, permissions]);
  const changeProfile = useCallback((key: DashboardProfileKey) => {
    setProfile(key);
    try { window.localStorage.setItem(storageKey, key); } catch { /* egal */ }
  }, [storageKey]);
  const activeProfile = useMemo(() => profileByKey(profile), [profile]);
  const pageKey = `dashboard:${profile}`;

  const visibleActions = useMemo(() => {
    const filtered = ACTIONS.filter((action) => permissions.has(action.permission));
    const order = activeProfile.actionOrder;
    const rank = (key: string) => { const index = order.indexOf(key); return index < 0 ? order.length : index; };
    return [...filtered].sort((left, right) => rank(left.key) - rank(right.key));
  }, [permissions, activeProfile]);
  const builtInByKey = useMemo(() => new Map(visibleActions.map((action) => [action.key, action])), [visibleActions]);

  // Der „Anpassen"-Modus ist jetzt global (Zahnrad-Schalter), damit ein Admin ihn einmal einschaltet und
  // über alle Seiten hinweg bearbeitet. Nur Admins (canCustomize) sehen überhaupt die Bearbeiten-Oberfläche.
  const platform = usePlatform();
  const editing = platform.customizing && canCustomize;
  const [layout, setLayout] = useState<UiLayout>(emptyUiLayout);
  const [flash, setFlash] = useState(false);
  const [draftOrder, setDraftOrder] = useState<readonly string[]>([]);
  const [draftTexts, setDraftTexts] = useState<Record<string, string>>({});
  const [draftHidden, setDraftHidden] = useState<ReadonlySet<string>>(new Set());
  const [draftCustom, setDraftCustom] = useState<readonly CustomTile[]>([]);
  const [draftColumns, setDraftColumns] = useState(3);
  const [draftTileSize, setDraftTileSize] = useState<TileSize>('normal');
  const [draftColors, setDraftColors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    layoutGateway.loadLayout(tenantId, pageKey).then((loaded) => { if (active) setLayout(loaded); }).catch(() => {});
    return () => { active = false; };
  }, [layoutGateway, tenantId, pageKey]);

  // Anzeige-Kacheln aus dem gespeicherten Layout (eingebaute + eigene).
  const displayTiles = useMemo<readonly DisplayTile[]>(() => {
    const builtIn: DisplayTile[] = visibleActions.map((action) => ({
      key: action.key,
      label: textOverride(layout, labelKey(action.key), action.label),
      hint: textOverride(layout, hintKey(action.key), action.hint),
      icon: action.icon,
      target: action.route,
      primary: !!action.primary,
      isCustom: false,
    }));
    const custom: DisplayTile[] = layout.customTiles.map((tile) => ({
      key: tile.key, label: tile.label, hint: tile.hint, icon: iconFor(tile.icon), target: tile.target, primary: false, isCustom: true,
    }));
    return [...builtIn, ...custom];
  }, [visibleActions, layout]);

  const hiddenSet = useMemo(() => new Set(layout.hidden), [layout]);
  const visibleOrdered = useMemo(
    () => applyLayoutOrder(displayTiles, layout.order).filter((tile) => !hiddenSet.has(tile.key)),
    [displayTiles, layout.order, hiddenSet],
  );

  const setText = useCallback((key: string, value: string) => setDraftTexts((current) => ({ ...current, [key]: value })), []);
  const patchCustom = useCallback((key: string, patch: Partial<CustomTile>) => setDraftCustom((current) => current.map((tile) => tile.key === key ? { ...tile, ...patch } : tile)), []);

  // Beim Betreten des Bearbeiten-Modus (und nach Laden/Speichern/Zurücksetzen, wenn sich `layout` ändert)
  // den Entwurf aus dem gespeicherten Layout aufbauen. Während des Ziehens/Tippens ändert sich nur der
  // Entwurf, nicht `layout` -- der Effekt greift also nicht in laufende Änderungen ein.
  useEffect(() => {
    if (!editing) return;
    const ordered = applyLayoutOrder(displayTiles, layout.order);
    setDraftOrder(ordered.map((tile) => tile.key));
    setDraftTexts({ ...layout.texts });
    setDraftHidden(new Set(layout.hidden));
    setDraftCustom(layout.customTiles.map((tile) => ({ ...tile })));
    setDraftColumns(layout.columns);
    setDraftTileSize(layout.tileSize);
    setDraftColors({ ...layout.colors });
    setError(null);
  }, [editing, displayTiles, layout]);

  const toggleHidden = useCallback((key: string) => {
    setDraftHidden((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const setColor = useCallback((key: string, value: string | null) => {
    setDraftColors((current) => {
      const next = { ...current };
      if (value) next[key] = value; else delete next[key];
      return next;
    });
  }, []);

  const addCustomTile = useCallback(() => {
    const key = `custom:${crypto.randomUUID()}`;
    setDraftCustom((current) => [...current, { key, label: '', hint: '', icon: 'Link', target: '' }]);
    setDraftOrder((current) => [...current, key]);
  }, []);

  const removeCustomTile = useCallback((key: string) => {
    setDraftCustom((current) => current.filter((tile) => tile.key !== key));
    setDraftOrder((current) => current.filter((entry) => entry !== key));
    setDraftHidden((current) => { const next = new Set(current); next.delete(key); return next; });
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setDraftOrder((current) => {
      const oldIndex = current.indexOf(String(active.id));
      const newIndex = current.indexOf(String(over.id));
      if (oldIndex < 0 || newIndex < 0) return current;
      return arrayMove([...current], oldIndex, newIndex);
    });
  }, []);

  const collectOverrides = useCallback((): Record<string, string> => {
    const overrides: Record<string, string> = {};
    const set = (key: string, def: string) => {
      const value = (draftTexts[key] ?? '').trim();
      if (value && value !== def) overrides[key] = value;
    };
    set(HEADING_ACTIONS, DEFAULT_HEADING_ACTIONS);
    set(HEADING_TODAY, DEFAULT_HEADING_TODAY);
    for (const action of visibleActions) {
      set(labelKey(action.key), action.label);
      set(hintKey(action.key), action.hint);
    }
    return overrides;
  }, [draftTexts, visibleActions]);

  const save = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      // Eigene Kacheln nur speichern, wenn Beschriftung UND Ziel gesetzt sind.
      const customTiles = draftCustom
        .map((tile) => ({ ...tile, label: tile.label.trim(), hint: tile.hint.trim(), target: tile.target.trim() }))
        .filter((tile) => tile.label && tile.target);
      const keptKeys = new Set([...visibleActions.map((action) => action.key), ...customTiles.map((tile) => tile.key)]);
      const order = draftOrder.filter((key) => keptKeys.has(key));
      const colors: Record<string, string> = {};
      for (const [key, value] of Object.entries(draftColors)) if (keptKeys.has(key)) colors[key] = value;
      const saved = await layoutGateway.saveLayout(tenantId, pageKey, {
        order, texts: collectOverrides(), hidden: [...draftHidden].filter((key) => keptKeys.has(key)), customTiles,
        columns: draftColumns, tileSize: draftTileSize, colors,
      });
      setLayout(saved);
      setFlash(true);
      window.setTimeout(() => setFlash(false), 1800);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Die Anpassung konnte nicht gespeichert werden.');
    } finally {
      setSaving(false);
    }
  }, [layoutGateway, tenantId, pageKey, draftOrder, draftHidden, draftCustom, draftColumns, draftTileSize, draftColors, collectOverrides, visibleActions]);

  const resetToDefault = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      const saved = await layoutGateway.saveLayout(tenantId, pageKey, emptyUiLayout);
      setLayout(saved);
      setDraftOrder(visibleActions.map((action) => action.key));
      setDraftTexts({});
      setDraftHidden(new Set());
      setDraftCustom([]);
      setDraftColumns(3);
      setDraftTileSize('normal');
      setDraftColors({});
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Die Anpassung konnte nicht zurückgesetzt werden.');
    } finally {
      setSaving(false);
    }
  }, [layoutGateway, tenantId, pageKey, visibleActions]);

  const openTile = (target: string) => {
    if (/^https?:\/\//i.test(target)) window.open(target, '_blank', 'noopener,noreferrer');
    else if (target) onNavigate(target);
  };

  const heading = (key: string, fallback: string) => (editing ? (
    <input className="forq-start__label-input" value={draftTexts[key] ?? fallback} onChange={(event) => setText(key, event.target.value)} aria-label="Überschrift bearbeiten" />
  ) : (
    <h2 className="forq-start__label">{textOverride(layout, key, fallback)}</h2>
  ));

  // Im Anpassen-Modus wird nach der Draft-Reihenfolge gerendert (eingebaute + eigene gemischt).
  const draftCustomByKey = useMemo(() => new Map(draftCustom.map((tile) => [tile.key, tile])), [draftCustom]);
  const editKeys = editing ? draftOrder.filter((key) => builtInByKey.has(key) || draftCustomByKey.has(key)) : [];
  const gridCols = editing ? draftColumns : layout.columns;
  const gridSize = editing ? draftTileSize : layout.tileSize;
  const gridStyle = { '--forq-start-cols': String(gridCols) } as CSSProperties;
  const gridSizeClass = `is-size-${gridSize}`;

  return (
    <div className="forq-start">
      <header className="forq-start__head">
        <h1>{hello}</h1>
        <p>{dateLabel}</p>
      </header>

      {profiles.length > 1 ? (
        <nav className="forq-start__profiles" aria-label="Dashboard-Ansicht">
          {profiles.map((entry) => (
            <button type="button" key={entry.key} className={entry.key === profile ? 'is-active' : ''} onClick={() => changeProfile(entry.key)}>
              {entry.icon}<span>{entry.label}</span>
            </button>
          ))}
        </nav>
      ) : null}

      <button type="button" className="forq-start__search" onClick={onOpenCommand}>
        <Search size={19} />
        <span>Kunde, Maschine, Auftrag suchen …</span>
        <kbd>Strg&nbsp;K</kbd>
      </button>

      {activeProfile.kpis.length > 0 ? (
        <section className="forq-start__kpis" aria-label="Kennzahlen">
          {activeProfile.kpis.map((kpi) => {
            const tone = kpi.tone?.(metrics) ?? 'normal';
            const note = kpi.note?.(metrics);
            return (
              <button type="button" key={kpi.key} className={`forq-start__kpi is-${tone}`} onClick={() => onNavigate(kpi.route)}>
                <span className="forq-start__kpi-icon">{kpi.icon}</span>
                <span className="forq-start__kpi-body">
                  <small>{kpi.label}</small>
                  <b>{kpi.value(metrics)}</b>
                  {note ? <em>{note}</em> : null}
                </span>
              </button>
            );
          })}
        </section>
      ) : null}

      <section className="forq-start__section" aria-label={DEFAULT_HEADING_ACTIONS}>
        <div className="forq-start__section-head">
          {heading(HEADING_ACTIONS, DEFAULT_HEADING_ACTIONS)}
          {canCustomize ? (
            editing ? (
              <div className="forq-start__customize-actions">
                <button type="button" onClick={addCustomTile} disabled={saving}><Plus size={14} /> Kachel</button>
                <button type="button" onClick={() => void resetToDefault()} disabled={saving}><RotateCcw size={14} /> Standard</button>
                {flash ? <span className="forq-start__saved"><Check size={14} /> Gespeichert</span> : null}
                <button type="button" className="is-primary" onClick={() => void save()} disabled={saving}><Check size={14} /> {saving ? 'Speichert …' : 'Speichern'}</button>
              </div>
            ) : (
              <button type="button" className="forq-start__customize" onClick={() => platform.setCustomizing(true)}><Pencil size={14} /> Anpassen</button>
            )
          ) : null}
        </div>
        {editing ? <p className="forq-start__customize-hint">Am Griff ziehen zum Umsortieren · in die Felder klicken für die Texte · Auge zum Aus-/Einblenden · „+ Kachel" für einen eigenen Shortcut. Gilt danach für alle im Betrieb.</p> : null}
        {editing ? (
          <div className="forq-start__display-control">
            <span>Spalten</span>
            {[2, 3, 4].map((columns) => (
              <button type="button" key={columns} className={draftColumns === columns ? 'is-active' : ''} onClick={() => setDraftColumns(columns)}>{columns}</button>
            ))}
            <span className="forq-start__display-sep">Größe</span>
            {SIZE_ORDER.map((size) => (
              <button type="button" key={size} className={draftTileSize === size ? 'is-active' : ''} onClick={() => setDraftTileSize(size)}>{SIZE_LABELS[size]}</button>
            ))}
          </div>
        ) : null}
        {error ? <div className="forq-start__customize-error" role="alert">{error}</div> : null}

        {editing ? (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={editKeys} strategy={rectSortingStrategy}>
              <div className={`forq-start__grid is-editing ${gridSizeClass}`} style={gridStyle}>
                {editKeys.map((key) => {
                  const custom = draftCustomByKey.get(key);
                  const action = builtInByKey.get(key);
                  return (
                    <StartEditTile
                      key={key}
                      id={key}
                      action={action}
                      custom={custom}
                      hidden={draftHidden.has(key)}
                      color={draftColors[key]}
                      labelValue={draftTexts[labelKey(key)] ?? action?.label ?? ''}
                      hintValue={draftTexts[hintKey(key)] ?? action?.hint ?? ''}
                      onToggleHidden={toggleHidden}
                      onSetColor={setColor}
                      onSetLabel={(tileKey, value) => setText(labelKey(tileKey), value)}
                      onSetHint={(tileKey, value) => setText(hintKey(tileKey), value)}
                      onPatchCustom={patchCustom}
                      onRemoveCustom={removeCustomTile}
                    />
                  );
                })}
              </div>
            </SortableContext>
          </DndContext>
        ) : (
          <div className={`forq-start__grid ${gridSizeClass}`} style={gridStyle}>
            {visibleOrdered.map((tile) => (
              <button type="button" key={tile.key} className={`forq-start__tile${tile.primary ? ' is-primary' : ''}`} style={layout.colors[tile.key] ? tileColorStyle(layout.colors[tile.key]) : undefined} onClick={() => openTile(tile.target)} aria-label={tile.label}>
                <span className="forq-start__tile-icon">{tile.icon}</span>
                <span className="forq-start__tile-text">
                  <b>{tile.label}</b>
                  <small>{tile.hint}</small>
                </span>
              </button>
            ))}
          </div>
        )}
      </section>

      <section className="forq-start__section" aria-label={DEFAULT_HEADING_TODAY}>
        {heading(HEADING_TODAY, DEFAULT_HEADING_TODAY)}
        {today.length === 0 ? (
          <div className="forq-start__empty">
            <CheckCircle2 size={20} /> Nichts Offenes — alles erledigt.
          </div>
        ) : (
          <ul className="forq-start__today">
            {today.map((task) => (
              <li key={task.id}>
                <button type="button" onClick={() => onNavigate(task.route)}>
                  <span className={`forq-start__dot is-${task.tone}`} aria-hidden="true" />
                  <span className="forq-start__today-text">
                    <b>{task.title}</b>
                    <small>{task.meta}</small>
                  </span>
                  <ChevronRight size={17} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
